import { rumService } from '../services/rum';
import logger from './logger';
import { AppConfig } from '../types/app';

/**
 * Generates a deterministic hash number from a string.
 * Uses a polynomial rolling hash algorithm with overflow protection.
 * Same input always produces the same output.
 *
 * @param str - The string to hash
 * @returns a positive integer hash value
 */
function hashString(str: string): number {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    // (hash << 5) shifts left by 5 bits, equivalent to multiplying by 32
    // Subtracting hash gives us: 32 * hash - hash = 31 * hash
    // Add the character's numeric value
    // | 0 forces the result to a 32-bit signed integer, preventing overflow
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }

  // Return absolute value to ensure positive number
  return Math.abs(hash);
}

/**
 * Determines if a feature should be used based on an ID and rollout percentage.
 * The ID is hashed deterministically so the same ID always lands in the same
 * bucket — raising the percentage only ever adds IDs, nobody drops out.
 *
 * @param id - The identifier to bucket (user ID, client_id, etc.)
 * @param rolloutPercent - The desired rollout percentage (0-100)
 * @returns true if the feature should be used, false otherwise
 */
export function shouldUseFeature(id: string, rolloutPercent: number): boolean {
  if (rolloutPercent === 100) return true;
  if (!id || rolloutPercent === 0) return false;
  return hashString(id) % 100 < rolloutPercent;
}

const SIMPLIFIED_SIGNIN_SEEN_KEY = 'simplifiedSignInSeen';
const SIMPLIFIED_SIGNIN_STORAGE = localStorage;

/**
 * Parses a comma-separated SSM parameter value into a Set of trimmed,
 * non-empty strings.
 */
function parseClientList(raw?: string): Set<string> {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  );
}

/**
 * Implements the Option C rollout decision for Simplified Sign-In.
 *
 * Decision order (first match wins):
 *  1. Master gate (`enableSimplifiedSignIn`) must be 'true' — otherwise classic.
 *  2. Hold-back list — if the app's client_id is in the list → classic.
 *  3. Early-access list — if the app's client_id is in the list → new UI.
 *  4. Percentage — bucket the client_id through shouldUseFeature.
 *  5. Session stickiness — if the user has previously seen the new UI in this
 *     browser session (any app), keep showing it.
 *  6. Otherwise → classic.
 *
 * @param config - The application config (from settings.json)
 * @returns true if the simplified sign-in UI should be shown
 */
export function shouldShowSimplifiedSignIn(config: AppConfig): boolean {
  // Master gate
  if (config.enableSimplifiedSignIn !== 'true') return false;

  const clientId = new URLSearchParams(window.location.search).get('client_id');

  const holdBackClients = parseClientList(
    config.simplifiedSignInHoldBackClients
  );
  const earlyAccessClients = parseClientList(
    config.simplifiedSignInEarlyAccessClients
  );
  const rolloutPercent = Number(config.simplifiedSignInRolloutPercent) || 0;

  let decision = false;
  let reason = 'default';

  if (clientId && holdBackClients.has(clientId)) {
    decision = false;
    reason = 'holdback';
  } else if (clientId && earlyAccessClients.has(clientId)) {
    decision = true;
    reason = 'early-access';
  } else if (clientId && shouldUseFeature(clientId, rolloutPercent)) {
    decision = true;
    reason = 'percentage';
  } else if (
    SIMPLIFIED_SIGNIN_STORAGE.getItem(SIMPLIFIED_SIGNIN_SEEN_KEY) === 'true'
  ) {
    decision = true;
    reason = 'browser-sticky';
  }

  // Persist so this browser always sees the new UI once exposed
  if (decision) {
    SIMPLIFIED_SIGNIN_STORAGE.setItem(SIMPLIFIED_SIGNIN_SEEN_KEY, 'true');
  }

  rumService.recordEvent('SimplifiedSignInRolloutDecision', {
    decision,
    reason,
    clientId: clientId || '',
    rolloutPercent,
  });

  logger.info(
    `Simplified sign-in rollout: decision=${decision}, reason=${reason}, clientId=${clientId}`
  );

  return decision;
}

/**
 * Determines if a custom domain should be used based on rollout percentage.
 *
 * @param featureName - Name used for RUM event and logging
 * @param customDomain - Custom domain name
 * @param rolloutPercent - Rollout percentage
 * @returns true if the custom domain should be used, false otherwise
 */
export function shouldUseCustomDomain(
  featureName: string,
  customDomain?: string,
  rolloutPercent?: number
): boolean {
  const userId = rumService.getUserId();
  const shouldUseCustomDomain =
    !!customDomain &&
    !!rolloutPercent &&
    shouldUseFeature(userId || '', rolloutPercent);

  rumService.recordEvent(`${featureName}_custom_domain_rollout`, {
    shouldUseCustomDomain: shouldUseCustomDomain,
    userId: userId,
    customDomain: customDomain,
    rolloutPercent: rolloutPercent,
  });

  logger.info(`Use ${featureName} custom domain: ${shouldUseCustomDomain}`);

  return shouldUseCustomDomain;
}
